import api from "../../../core/api";
import { Page } from "../../../shared/models/models";
import {
  AddAssigneesRequest,
  AddProgressUpdate,
  AnalyticsQuery,
  AssignTeamRequest,
  AssignWorkOrder,
  CancelFutureResponse,
  ChangeAssigneeRoleRequest,
  ChangeWorkOrderStatus,
  CreateWorkOrder,
  EscalateResponse,
  EscalateWorkOrder,
  GenerateRecurringResponse,
  KpiSummary,
  PreviewRecurrenceRequest,
  PreviewRecurrenceResponse,
  RegenerateFutureResponse,
  UpdateWorkOrder,
  WorkOrder,
  WorkOrderAnalytics,
  WorkOrderDetail,
  WorkOrderFilters,
  WorkOrderProgressUpdate,
  WorkOrderSummary,
  WorkOrdersQuery,
} from "../models/models";

const getRequestConfig = () => {
  return { baseURL: (window as any).app.config.maintenanceApiBaseUrl };
};

const serializeParams = (params: any): string => {
  const parts: string[] = [];

  Object.keys(params).forEach(key => {
    const value = params[key];

    if (Array.isArray(value)) {
      value.forEach(item => {
        parts.push(`${key}=${encodeURIComponent(item)}`);
      });
    } else if (value !== undefined && value !== null) {
      parts.push(`${key}=${encodeURIComponent(value)}`);
    }
  });

  return parts.join("&");
};

class WorkOrdersApi {
  getWorkOrders(query: WorkOrdersQuery): Promise<Page<WorkOrder>> {
    return api
      .get<Page<WorkOrder>>("/v1/customer/work-orders", {
        ...getRequestConfig(),
        params: query,
        paramsSerializer: serializeParams,
      })
      .then(x => x.data);
  }

  getWorkOrder(id: string): Promise<WorkOrderDetail> {
    return api
      .get<WorkOrderDetail>(`/v1/customer/work-orders/${id}`, getRequestConfig())
      .then(x => x.data);
  }

  createWorkOrder(model: CreateWorkOrder): Promise<WorkOrderDetail> {
    return api
      .post<WorkOrderDetail>("/v1/customer/work-orders", model, getRequestConfig())
      .then(x => x.data);
  }

  updateWorkOrder(id: string, model: UpdateWorkOrder): Promise<WorkOrderDetail> {
    return api
      .put<WorkOrderDetail>(`/v1/customer/work-orders/${id}`, model, getRequestConfig())
      .then(x => x.data);
  }

  changeStatus(id: string, model: ChangeWorkOrderStatus): Promise<WorkOrderDetail> {
    return api
      .post<WorkOrderDetail>(`/v1/customer/work-orders/${id}/status`, model, getRequestConfig())
      .then(x => x.data);
  }

  editCompletionDetails(
    id: string,
    completionNotes: string,
    cost: number | undefined
  ): Promise<void> {
    return api
      .patch(
        `/v1/customer/work-orders/${id}/completion-details`,
        { completionNotes, cost },
        getRequestConfig()
      )
      .then(() => {});
  }

  assignWorkOrder(id: string, model: AssignWorkOrder): Promise<WorkOrderDetail> {
    return api
      .post<WorkOrderDetail>(`/v1/customer/work-orders/${id}/assign`, model, getRequestConfig())
      .then(x => x.data);
  }

  addProgressUpdate(id: string, model: AddProgressUpdate): Promise<WorkOrderProgressUpdate> {
    return api
      .post<WorkOrderProgressUpdate>(
        `/v1/customer/work-orders/${id}/progress`,
        model,
        getRequestConfig()
      )
      .then(x => x.data);
  }

  getProgressUpdates(id: string): Promise<WorkOrderProgressUpdate[]> {
    return api
      .get<WorkOrderProgressUpdate[]>(
        `/v1/customer/work-orders/${id}/progress`,
        getRequestConfig()
      )
      .then(x => x.data);
  }

  uploadFile(
    id: string,
    file: File,
    context?: "General" | "ClosingRemarks"
  ): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);
    const query = context ? `?context=${context}` : "";
    return api
      .post(`/v1/customer/work-orders/${id}/files${query}`, formData, {
        ...getRequestConfig(),
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(x => x.data);
  }

  deleteFile(id: string, fileId: string): Promise<any> {
    return api
      .delete(`/v1/customer/work-orders/${id}/files/${fileId}`, getRequestConfig())
      .then(x => x.data);
  }

  getSummary(): Promise<WorkOrderSummary> {
    return api
      .get<WorkOrderSummary>("/v1/customer/work-orders/summary", getRequestConfig())
      .then(x => x.data);
  }

  getFilters(): Promise<WorkOrderFilters> {
    return api
      .get<WorkOrderFilters>("/v1/customer/work-orders/filters", getRequestConfig())
      .then(x => x.data);
  }

  getMyTasks(): Promise<WorkOrder[]> {
    return api
      .get<WorkOrder[]>("/v1/customer/work-orders/my-tasks", getRequestConfig())
      .then(x => x.data);
  }

  generateRecurring(): Promise<GenerateRecurringResponse> {
    return api
      .post<GenerateRecurringResponse>(
        "/v1/customer/work-orders/generate-recurring",
        {},
        getRequestConfig()
      )
      .then(x => x.data);
  }

  previewRecurrence(request: PreviewRecurrenceRequest): Promise<PreviewRecurrenceResponse> {
    return api
      .post<PreviewRecurrenceResponse>(
        "/v1/customer/work-orders/preview-recurrence",
        request,
        getRequestConfig()
      )
      .then(x => x.data);
  }

  getAnalytics(query: AnalyticsQuery): Promise<WorkOrderAnalytics> {
    return api
      .get<WorkOrderAnalytics>("/v1/customer/work-orders/analytics", {
        ...getRequestConfig(),
        params: query,
      })
      .then(x => x.data);
  }

  getKpiSummary(): Promise<KpiSummary> {
    return api
      .get<KpiSummary>("/v1/customer/work-orders/kpi-summary", getRequestConfig())
      .then(x => x.data);
  }

  escalateToServiceBoard(id: string, model: EscalateWorkOrder): Promise<EscalateResponse> {
    return api
      .post<EscalateResponse>(
        `/v1/customer/work-orders/${id}/escalate`,
        model,
        getRequestConfig()
      )
      .then(x => x.data);
  }

  // ─── Recurring series endpoints ──────────────────────────────

  regenerateFutureInstances(templateId: string): Promise<RegenerateFutureResponse> {
    return api
      .post<RegenerateFutureResponse>(
        `/v1/customer/work-orders/${templateId}/regenerate-future`,
        {},
        getRequestConfig()
      )
      .then(x => x.data);
  }

  cancelFutureInstances(templateId: string): Promise<CancelFutureResponse> {
    return api
      .post<CancelFutureResponse>(
        `/v1/customer/work-orders/${templateId}/cancel-future`,
        {},
        getRequestConfig()
      )
      .then(x => x.data);
  }

  // ─── Multi-Assignee endpoints ────────────────────────────────

  addAssignees(workOrderId: string, request: AddAssigneesRequest): Promise<WorkOrderDetail> {
    return api
      .post<WorkOrderDetail>(
        `/v1/customer/work-orders/${workOrderId}/assignees`,
        request,
        getRequestConfig()
      )
      .then(x => x.data);
  }

  removeAssignee(workOrderId: string, userId: string): Promise<WorkOrderDetail> {
    return api
      .delete<WorkOrderDetail>(
        `/v1/customer/work-orders/${workOrderId}/assignees/${userId}`,
        getRequestConfig()
      )
      .then(x => x.data);
  }

  changeAssigneeRole(
    workOrderId: string,
    userId: string,
    request: ChangeAssigneeRoleRequest
  ): Promise<WorkOrderDetail> {
    return api
      .put<WorkOrderDetail>(
        `/v1/customer/work-orders/${workOrderId}/assignees/${userId}/role`,
        request,
        getRequestConfig()
      )
      .then(x => x.data);
  }

  assignTeam(workOrderId: string, request: AssignTeamRequest): Promise<WorkOrderDetail> {
    return api
      .post<WorkOrderDetail>(
        `/v1/customer/work-orders/${workOrderId}/assign-team`,
        request,
        getRequestConfig()
      )
      .then(x => x.data);
  }

  reorderWorkOrders(statusId: number, workOrderIds: string[]): Promise<void> {
    return api
      .put("/v1/customer/work-orders/reorder", { statusId, workOrderIds }, getRequestConfig())
      .then(() => {});
  }
}

export default new WorkOrdersApi();

/**
 * WO-CLOSING-PHOTOS-0414: Build an absolute URL for fetching a WO file.
 * Used by `<img src>` (inline) and `window.open` (download) — both bypass axios,
 * so they need the fully-qualified maintenance service URL, not a relative path.
 */
export function getWorkOrderFileUrl(fileId: string, download = false): string {
  const baseUrl = (window as any).app.config.maintenanceApiBaseUrl as string;
  const suffix = download ? "/download" : "";
  return `${baseUrl}/v1/customer/work-orders/files/${fileId}${suffix}`;
}
